import { Fragment, useEffect, useMemo, useState } from "react";
import { StoryChartButton } from "./StoryChart";
import { api, getActiveBook, Manifest, setHoldingsPageScope } from "../api";
import { px, signedUsd, usd, usdCompact } from "../lib/money";
import { BOOK_LABEL } from "./Performance";
import { Money, MoneySwitch, ScopeNote } from "./MoneyScope";

// EVERYTHING the firm holds, rolled up by ASSET with the tranche breakdown
// beneath (Alan, 2026-08-18: "the various positions across assets is getting
// confusing… an aggregate summary of the asset with a breakdown by tranche
// might be a better use of space"). One row per asset: net size, weighted
// cost, one mark, P&L, exposure; expand it and every tranche that makes it
// up is there — which book, which ladder rung, its own entry/stop/target/P&L.
// Strikes and dials live on Plan and Funding — this page is "what do we have".

type Tranche = {
  id: string | null; book: string; tranche_id: string; entry_rule: string; strike: number | null;
  side: string; qty: number; entry: number; stop: number | null; target: number | null;
  mark: number | null; pnl: number | null; pnl_pct: number | null; opened_at: string | null;
  age_days: number | null; rationale: string; source: string; kind: "held" | "intraday";
};
type Asset = {
  symbol: string; sleeve: "equity" | "option" | "crypto" | "forex" | "commodity";
  net_qty: number; long_qty: number; short_qty: number; avg_cost: number; mark: number | null;
  unrealized: number | null; unrealized_pct: number | null; exposure: number | null;
  tranche_count: number; books: string[]; stops: number[]; targets: number[]; oldest_days: number | null;
  tranches: Tranche[];
};
type Rollup = { assets: Asset[]; books: string[]; as_of: string; is_paper: boolean; mixes_money: boolean };
type Leg = { currency: string; amount: number; usd: number | null; pct_of_nav: number | null; is_cash_only: boolean };
type FxExposure = {
  available: boolean; error?: string; account_currency: string; nav: number; balance: number;
  margin_used: number; gross_notional: number; leverage: number | null;
  away_from_account_currency_usd: number; away_from_account_currency_pct: number | null; legs: Leg[];
};
type Holdings = { budget: number; deployed: number; conversions_this_week: number };
type Conv = {
  symbol: string; entry_price: number; conversion_price: number;
  daily_loss_realized: number; stonky_statement: string | null; created_at: string;
};

const CLASS_LABEL: Record<string, string> = { equity: "Equity", option: "Option", crypto: "Crypto", forex: "Forex", commodity: "Commodity" };
const CLASS_TONE: Record<string, string> = {
  equity: "bg-accent/15 text-accent", option: "bg-slate-700 text-slate-200",
  crypto: "bg-gold/15 text-gold", forex: "bg-up/15 text-up", commodity: "bg-gold/25 text-gold",
};
const RULE_LABEL: Record<string, string> = {
  accumulate: "accumulate", dip: "dip rung", ladder: "ladder", board: "board", conversion: "rescue",
  desk: "day trade", manual: "manual",
};

export default function Holdings(_: { manifest: Manifest }) {
  const active = getActiveBook();
  const isGrowth = active.endsWith("long_term");
  const [scope, setScope] = useState<"book" | "all">("all");
  // All-books means all books OF ONE MONEY TYPE. Summing a paper tranche and
  // a real one into one asset row produces a P&L that is neither (2026-08-19).
  const [money, setMoney] = useState<Money>("real");
  const [roll, setRoll] = useState<Rollup | null>(null);
  const [h, setH] = useState<Holdings | null>(null);
  const [convs, setConvs] = useState<Conv[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [allOpen, setAllOpen] = useState(false);
  const [fx, setFx] = useState<FxExposure | null>(null);

  // Poll, don't fetch-once — fills and halts used to appear only after a
  // manual re-navigation (Kaizen D5/F7). Same pattern as the Blotter.
  useEffect(() => {
    setHoldingsPageScope(scope === "book" ? active : money);
    void load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, money]);
  async function load() {
    try {
      const q = scope === "book" ? `?books=${encodeURIComponent(active)}` : `?money=${money}`;
      setRoll(await api.get<Rollup>(`/api/trading/rollup${q}`));
      if (isGrowth) {
        setH(await api.get<Holdings>("/api/longterm/holdings"));
        setConvs(await api.get<Conv[]>("/api/longterm/conversions"));
      }
      setError(null);
    } catch (e) { setError((e as Error).message); }
    // Currency exposure is venue-level and only meaningful for real money.
    if (money === "real" || scope === "book") {
      api.get<FxExposure>("/api/forex/exposure").then(setFx).catch(() => setFx(null));
    } else { setFx(null); }
  }

  const assets = roll?.assets ?? [];
  const totals = useMemo(() => {
    const pnl = assets.reduce((s, a) => s + (a.unrealized ?? 0), 0);
    const exp = assets.reduce((s, a) => s + (a.exposure ?? 0), 0);
    const tr = assets.reduce((s, a) => s + a.tranche_count, 0);
    return { pnl, exp, tr };
  }, [assets]);
  const isOpen = (sym: string) => open[sym] ?? allOpen;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h1 className="text-lg font-bold">Holdings <span className="text-gold">{isGrowth ? "(Growth)" : "(Daily)"}</span></h1>
        <span className="text-xs text-slate-500">by asset, then by tranche</span>
        {roll && <ScopeNote book={scope === "book" ? active : "all"} money={money} isPaper={roll.is_paper} books={roll.books} />}
        <div className="ml-auto flex items-center gap-2">
          {scope === "all" && <MoneySwitch book="all" money={money} onChange={setMoney} />}
          <div className="flex gap-1 text-xs font-mono">
          {(["all", "book"] as const).map((s) => (
            <button key={s} onClick={() => setScope(s)}
              className={`px-2 py-0.5 rounded border ${scope === s ? "border-slate-400 text-slate-100" : "border-slate-700 text-slate-500 hover:text-slate-300"}`}>
              {s === "all" ? "all books" : "this book"}
            </button>
          ))}
          <button onClick={() => { setAllOpen(!allOpen); setOpen({}); }}
            className="px-2 py-0.5 rounded border border-slate-700 text-slate-500 hover:text-slate-300">
            {allOpen ? "collapse" : "expand"} tranches
          </button>
          </div>
        </div>
      </div>
      {error && <div className="text-down text-sm">{error}</div>}

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Assets" value={`${assets.length}`} sub={`${totals.tr} tranche${totals.tr === 1 ? "" : "s"}`} />
        <Stat label="Exposure" value={usdCompact(totals.exp)} sub="at current marks" />
        <Stat label="Open P&L" value={signedUsd(totals.pnl)} tone={totals.pnl >= 0 ? "text-up" : "text-down"} />
      </div>
      {h && isGrowth && (
        <div className="grid grid-cols-3 gap-3" title={`${BOOK_LABEL[active] ?? active} only`}>
          <Stat label="Growth budget" value={usdCompact(h.budget)} sub={BOOK_LABEL[active] ?? active} />
          <Stat label="Deployed" value={usdCompact(h.deployed)} sub={`${((h.deployed / (h.budget || 1)) * 100).toFixed(0)}% of slice`} />
          <Stat label="Rescues this week" value={`${h.conversions_this_week} / 1`} />
        </div>
      )}

      {fx?.available && fx.legs.length > 0 && <CurrencyExposure fx={fx} />}

      <div className="card">
        <h2 className="label">Open positions</h2>
        {!roll && !error && <div className="text-slate-600 text-sm">pricing every tranche…</div>}
        {roll && assets.length === 0 ? (
          <div className="text-slate-600 text-sm">nothing held</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-500 uppercase">
                  <th className="py-1 w-6"></th><th>Asset</th><th>Class</th><th>Net</th><th>Avg cost</th>
                  <th>Now</th><th>P&amp;L</th><th>Exposure</th><th>Tranches</th><th>Books</th>
                </tr></thead>
                <tbody className="font-mono">
                  {assets.map((a) => (
                    <Fragment key={`${a.symbol}:${a.sleeve}`}>
                      <tr className="border-t border-edge align-top cursor-pointer hover:bg-slate-800/40"
                          onClick={() => setOpen({ ...open, [a.symbol]: !isOpen(a.symbol) })}>
                        <td className="py-1.5 text-slate-500">{isOpen(a.symbol) ? "▾" : "▸"}</td>
                        <td className="py-1.5 font-bold">{a.symbol} {!String(a.symbol).includes("_") && <StoryChartButton symbol={a.symbol} entry={a.avg_cost ?? null} label="held" className="ml-1 font-normal" />}</td>
                        <td><span className={`badge ${CLASS_TONE[a.sleeve]}`}>{CLASS_LABEL[a.sleeve]}</span></td>
                        <td>{fmtQty(a.net_qty)}{a.short_qty > 0 && a.long_qty > 0 && <span className="text-slate-500 text-[10px]"> ({fmtQty(a.long_qty)}L/{fmtQty(a.short_qty)}S)</span>}</td>
                        <td>{px(a.symbol, a.avg_cost)}</td>
                        <td>{a.mark != null ? px(a.symbol, a.mark) : "—"}</td>
                        <td className={pnlTone(a.unrealized)}>{pnlText(a.unrealized)}{a.unrealized_pct != null && <span className="text-[10px] text-slate-500"> {a.unrealized_pct >= 0 ? "+" : ""}{a.unrealized_pct}%</span>}</td>
                        <td className="text-slate-300">{a.exposure != null ? usd(a.exposure) : "—"}</td>
                        <td className="text-slate-400">{a.tranche_count}{a.oldest_days != null && <span className="text-[10px] text-slate-600"> · oldest {a.oldest_days}d</span>}</td>
                        <td><BookChips books={a.books} /></td>
                      </tr>
                      {isOpen(a.symbol) && (
                        <tr className="bg-slate-900/40">
                          <td></td>
                          <td colSpan={9} className="pb-2">
                            <TrancheTable a={a} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-2">
              {assets.map((a) => (
                <div key={`${a.symbol}:${a.sleeve}`} className="rounded-lg border border-edge p-2.5">
                  <div className="flex items-center gap-2" onClick={() => setOpen({ ...open, [a.symbol]: !isOpen(a.symbol) })}>
                    <span className="font-mono font-bold">{a.symbol}</span>
                    <span className={`badge ${CLASS_TONE[a.sleeve]}`}>{CLASS_LABEL[a.sleeve]}</span>
                    <span className="text-xs text-slate-400">×{fmtQty(a.net_qty)}</span>
                    <span className={`ml-auto font-mono font-bold ${pnlTone(a.unrealized)}`}>{pnlText(a.unrealized)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 text-xs font-mono mt-1.5">
                    <KV k="avg cost" v={px(a.symbol, a.avg_cost)} />
                    <KV k="now" v={a.mark != null ? px(a.symbol, a.mark) : "—"} />
                    <KV k="exposure" v={a.exposure != null ? usd(a.exposure) : "—"} />
                    <KV k="tranches" v={`${a.tranche_count} · ${a.books.map((b) => BOOK_LABEL[b] ?? b).join(", ")}`} />
                  </div>
                  <button className="text-[11px] text-slate-500 mt-1.5 underline-offset-2 hover:underline"
                          onClick={() => setOpen({ ...open, [a.symbol]: !isOpen(a.symbol) })}>
                    {isOpen(a.symbol) ? "hide" : "show"} {a.tranche_count} tranche{a.tranche_count === 1 ? "" : "s"}
                  </button>
                  {isOpen(a.symbol) && (
                    <div className="mt-1.5 space-y-1.5">
                      {a.tranches.map((t, i) => (
                        <div key={t.id ?? i} className="rounded border border-slate-800 p-2 text-[11px] font-mono">
                          <div className="flex items-center gap-2">
                            <BookChips books={[t.book]} />
                            <span className="text-slate-400">{RULE_LABEL[t.entry_rule] ?? t.entry_rule}</span>
                            <span className="text-slate-500">{t.side} ×{fmtQty(t.qty)}</span>
                            <span className={`ml-auto ${pnlTone(t.pnl)}`}>{pnlText(t.pnl)}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 mt-1">
                            <KV k="entry" v={px(a.symbol, t.entry)} />
                            <KV k="age" v={t.age_days != null ? `${t.age_days}d` : "—"} />
                            <KV k="target" v={t.target != null ? px(a.symbol, t.target) : "—"} tone="text-gold" />
                            <KV k="stop" v={t.stop != null ? px(a.symbol, t.stop) : "—"} tone="text-down" />
                          </div>
                          {t.rationale && <div className="text-slate-500 font-sans mt-1">{t.rationale}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {isGrowth && convs.length > 0 && (
        <div className="card">
          <h2 className="label">Conversion ledger (scrutinized)</h2>
          <div className="space-y-2">
            {convs.map((c, i) => (
              <div key={i} className="border-l-2 border-gold pl-3 py-1 text-sm">
                <div className="flex flex-wrap gap-x-3 items-baseline">
                  <span className="font-mono font-bold">{c.symbol}</span>
                  <span className="text-slate-500 font-mono text-xs">
                    entry ${c.entry_price} → conv ${c.conversion_price}
                  </span>
                  <span className="text-down font-mono">day booked {usd(c.daily_loss_realized)}</span>
                  <span className="text-slate-600 text-xs ml-auto">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                {c.stonky_statement && <p className="text-slate-300 italic mt-1">&ldquo;{c.stonky_statement}&rdquo;</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// In FX, CASH IS A POSITION — an idle USD balance is a long-dollar bet. The
// deployed/free framing every other sleeve uses hides that, which is why this
// card leads with currency legs and demotes margin to a footnote.
function CurrencyExposure({ fx }: { fx: FxExposure }) {
  const away = fx.away_from_account_currency_pct ?? 0;
  return (
    <div className="card">
      <div className="flex items-baseline gap-2 flex-wrap">
        <h2 className="label mb-0">Currency exposure</h2>
        <span className="text-xs text-slate-500">
          what the FX venue actually holds — cash included, because idle {fx.account_currency} is a position
        </span>
        <span className="ml-auto text-xs font-mono text-slate-400">
          {away.toFixed(0)}% away from {fx.account_currency}
        </span>
      </div>
      <div className="mt-2 space-y-1.5">
        {fx.legs.map((l) => {
          const pct = l.pct_of_nav ?? 0;
          const isBase = l.currency === fx.account_currency;
          return (
            <div key={l.currency} className="flex items-center gap-2 text-xs font-mono">
              <span className="w-10 font-bold text-slate-200">{l.currency}</span>
              <div className="flex-1 h-3 bg-slate-800 rounded overflow-hidden">
                <div className={`h-full ${isBase ? "bg-slate-500" : "bg-gold/70"}`}
                     style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              <span className="w-24 text-right text-slate-300">
                {l.amount >= 0 ? "" : "−"}{Math.abs(l.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
              <span className="w-20 text-right text-slate-400">{l.usd != null ? usd(l.usd) : "—"}</span>
              <span className="w-12 text-right text-slate-500">{pct.toFixed(0)}%</span>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-slate-600 mt-2 font-mono">
        NAV {usd(fx.nav)} · notional {usd(fx.gross_notional)} ={" "}
        <span className={(fx.leverage ?? 0) > 1 ? "text-down" : "text-slate-400"}>
          {(fx.leverage ?? 0).toFixed(2)}× NAV
        </span>{" "}
        {(fx.leverage ?? 0) <= 1 && <span className="text-slate-600">(no leverage — notional below equity)</span>}
        {" "}· margin posted {usd(fx.margin_used)}
      </div>
    </div>
  );
}

function TrancheTable({ a }: { a: Asset }) {
  return (
    <table className="w-full text-xs mt-1">
      <thead><tr className="text-left text-[10px] text-slate-500 uppercase">
        <th className="py-0.5">Book</th><th>Tranche</th><th>Entered as</th><th>Side</th><th>Qty</th><th>Entry</th>
        <th>Target</th><th>Stop</th><th>P&amp;L</th><th>Age</th><th>Why</th>
      </tr></thead>
      <tbody>
        {a.tranches.map((t, i) => (
          <tr key={t.id ?? i} className="border-t border-slate-800/60 align-top">
            <td className="py-1"><BookChips books={[t.book]} /></td>
            <td className="text-slate-400 max-w-[11rem] truncate" title={t.tranche_id}>{t.tranche_id}</td>
            <td className="text-slate-300">{RULE_LABEL[t.entry_rule] ?? t.entry_rule}{t.strike != null && <span className="text-slate-500"> @ {px(a.symbol, t.strike)}</span>}</td>
            <td className="text-slate-400">{t.side}</td>
            <td>{fmtQty(t.qty)}</td>
            <td>{px(a.symbol, t.entry)}</td>
            <td className="text-gold">{t.target != null ? px(a.symbol, t.target) : "—"}</td>
            <td className="text-down">{t.stop != null ? px(a.symbol, t.stop) : "—"}</td>
            <td className={pnlTone(t.pnl)}>{pnlText(t.pnl)}{t.pnl_pct != null && <span className="text-[10px] text-slate-500"> {t.pnl_pct >= 0 ? "+" : ""}{t.pnl_pct}%</span>}</td>
            <td className="text-slate-500">{t.age_days != null ? `${t.age_days}d` : "—"}</td>
            <td className="font-sans text-slate-400 max-w-md">{t.rationale}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BookChips({ books }: { books: string[] }) {
  return (
    <span className="inline-flex gap-1 flex-wrap">
      {books.map((b) => (
        <span key={b} className={`badge ${b.startsWith("live") ? "bg-down/15 text-down" : "bg-slate-700 text-slate-300"}`}>
          {BOOK_LABEL[b] ?? b}
        </span>
      ))}
    </span>
  );
}
function fmtQty(n: number): string {
  return Math.abs(n) >= 1000 ? n.toFixed(0) : Math.abs(n) >= 1 ? String(+n.toFixed(4)) : String(+n.toFixed(8));
}
function pnlTone(n: number | null): string {
  return n == null ? "" : n >= 0 ? "text-up" : "text-down";
}
function pnlText(n: number | null): string {
  return n != null ? signedUsd(n) : "—";
}
function KV({ k, v, tone }: { k: string; v: string; tone?: string }) {
  return <div className="flex justify-between gap-2"><span className="text-slate-500 font-sans">{k}</span><span className={`${tone ?? ""} truncate`}>{v}</span></div>;
}
function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="card py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-lg font-bold font-mono ${tone ?? ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-600">{sub}</div>}
    </div>
  );
}
